import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import VendorRolloutStatusBoard from "legal-entities/vendor-rollouts/_views/vendor-rollout-status-board";
import ViewCompanyProfile from "marketplace/companies/profiles/_views/view-company-profile";
import "permissions/__extensions/users/user-extensions";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLoaderData, useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import { useGetAppVersionQuery } from "users/super-users/_api/super-user-api";
import { latestDeploymentVersion } from "users/super-users/deployment-version";
import EntitiesPage from "users/super-users/_views/entity-administration/entities-page";
import { PageTab } from "users/super-users/_views/super-user-administration-panel-tabs";
import SystemNotifications from "users/super-users/_views/system-notifications/system-notifications-table";
import ActiveUserInvitationsPage from "users/super-users/_views/user-administration/user-invitations-page";
import UsersAdmin from "users/super-users/_views/user-administration/users-table";
import OnboardingTemplatesPage from "work/onboarding-templates/_views/onboarding-templates-page";

const PageContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    padding: 0,
  },
  display: "flex",
  flexDirection: "column",
  height: "100%",
  marginTop: theme.spacing(1),
  padding: theme.spacing(1),
  width: "100%",
}));
const PageTitle = styled(Typography)({
  marginBottom: "1rem",
  textAlign: "center",
});
const Version = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[500],
  fontSize: "1.2em",
  marginTop: theme.spacing(2),
  textAlign: "center",
}));

export default function SuperUserDashboard() {
  const routeParams = useLoaderData();
  const activeTab: PageTab = Object.values(PageTab).includes(routeParams.pageTab) ? routeParams.pageTab : PageTab.Users;
  const session = useSession();
  const appVersionQuery = useGetAppVersionQuery(undefined, {
    skip: !session.user?.canAdministerPlatform(),
  });
  const appVersion = latestDeploymentVersion(
    appVersionQuery.data,
    import.meta.env.VITE_CIRCLECI_PIPELINE_NUMBER,
  );
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);
  const dispatch = useAttorneyHubDispatch();

  const navigate = useNavigate();
  const { openDialog } = useDialog();

  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  useEffect(() => {
    if (!Object.values(PageTab).includes(routeParams.pageTab)) {
      navigate(`/super-user/${PageTab.Users}`);
      return;
    }

    loadRouteProfile(routeParams.profileType, routeParams.id);

    function loadRouteProfile(profileType?: string, id?: string) {
      if (!profileType || !id) return;

      const guidId = new Guid(id);
      const parentPath = new URL("..", globalThis.location.origin + globalThis.location.pathname);

      if (profileType === "company") {
        openDialog({
          // The route is what asked for this, and it asks again on every render: `routeParams` is a
          // fresh object from `useParams()` each time, so this effect's dependencies never settle.
          // The key is what lets the provider tell the repeats apart from a second visit.
          dialogKey: `super-user-profile:${profileType}:${guidId.value}`,
          component: <ViewCompanyProfile companyId={guidId} />,
          MuiProps: {
            fullWidth: true,
            maxWidth: "lg",
          },
          contentSxProps: {
            display: "flex",
          },
          titleStyle: {
            paddingBottom: 0,
          },
          onClose: () => {
            navigate(parentPath.pathname);
          },
        });
      } else {
        console.warn(`Unknown profile type: ${profileType}`);
      }
    }
  }, [routeParams, navigate, openDialog]);

  return (
    <PageContainer>
      <PageTitle id="super-user-dashboard-header" variant="h4">
        Superuser Dashboard
      </PageTitle>
      <Tabs
        variant="fullWidth"
        indicatorColor="primary"
        value={activeTab}
        onChange={(event: React.SyntheticEvent, newValue: string) => navigate(`/super-user/${newValue}`)}
        textColor="primary"
      >
        <Tab id="super-user-users-tab" label="Users" value="users" />
        <Tab id="super-user-entities-tab" label="Entities" value="entities" />
        <Tab id="super-user-invitations-tab" label="Invitations" value="invitations" />
        <Tab id="super-user-onboarding-templates-tab" label="Onboarding Templates" value="onboarding-templates" />
        <Tab id="super-user-firm-onboarding-tab" label="Firm Onboarding" value="firm-onboarding" />
        <Tab id="super-user-notifications-tab" label="System Notifications" value="system-notifications" />
      </Tabs>
      {activeTab === PageTab.Users && (
        <UsersAdmin
          onProfileView={(profileType?: string, id?: string) => {
            if (!profileType || !id) return;
            navigate(`/super-user/${PageTab.Users}/${profileType}/${id}`);
          }}
        />
      )}
      {activeTab === PageTab.Entities && <EntitiesPage />}
      {activeTab === PageTab.Invitations && <ActiveUserInvitationsPage />}
      {activeTab === PageTab.OnboardingTemplates && <OnboardingTemplatesPage />}
      {/* No client id: staff watch every client's roll-outs at once, which is what the platform
          grant is for. Scoping to one client is the client's own page. */}
      {activeTab === PageTab.FirmOnboarding && (
        <VendorRolloutStatusBoard
          emptyTitle="No firm roll-outs yet"
          emptyDescription="Roll-outs appear here once a client's officer invitations go out."
        />
      )}
      {activeTab === PageTab.SystemNotifications && <SystemNotifications />}
      <Version>Version: {appVersion}</Version>
    </PageContainer>
  );
}
